import React, { useEffect, useState } from 'react';
import { contentAPI } from '../services/api';
import SearchModal from '../components/SearchModal';
import { Search, Filter, Play, Lock, Star, DollarSign } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import FavoriteButton from '../components/FavoriteButton';

const MusicVideos = () => {
  const navigate = useNavigate();
  const [videos, setVideos] = useState([]);
  const [filteredVideos, setFilteredVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [selectedGenre, setSelectedGenre] = useState('all');
  const [genres, setGenres] = useState([]);

  useEffect(() => {
    loadVideos();
  }, []);

  useEffect(() => {
    filterVideos();
  }, [selectedGenre, videos]);

  const loadVideos = async () => {
    try {
      const response = await contentAPI.getAll({ content_type: 'music_video', limit: 100 });
      setVideos(response.data);
      
      // Extract unique genres
      const allGenres = new Set();
      response.data.forEach(video => {
        video.genre.forEach(g => allGenres.add(g));
      });
      setGenres(Array.from(allGenres).sort());
    } catch (error) {
      console.error('Error loading music videos:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterVideos = () => {
    let filtered = [...videos];

    // Genre filter
    if (selectedGenre !== 'all') {
      filtered = filtered.filter(video => video.genre.includes(selectedGenre));
    }

    setFilteredVideos(filtered);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-electric/30 border-t-electric rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8" data-testid="music-videos-page">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-foreground mb-2">Videos</h1>
          <p className="text-muted-foreground">Visual artistry meets musical talent</p>
        </div>

        {/* Filters */}
        <div className="mb-8 space-y-4">
          {/* Search Button */}
          <button
            onClick={() => setIsSearchOpen(true)}
            className="w-full flex items-center space-x-3 px-6 py-4 bg-white/5 backdrop-blur-3xl border border-white/20 rounded-xl hover:bg-white/10 hover:border-electric/40 transition-all shadow-xl group"
            style={{ backdropFilter: 'blur(30px) saturate(180%)' }}
            data-testid="open-search-button"
          >
            <Search className="h-5 w-5 text-electric group-hover:scale-110 transition-transform" />
            <span className="text-foreground/60 text-left flex-1">Search music videos...</span>
            <kbd className="px-3 py-1 rounded-lg bg-white/10 border border-white/20 text-xs text-foreground/60">⌘K</kbd>
          </button>

          {/* Genre Filter */}
          <div className="flex items-center space-x-3 overflow-x-auto pb-2">
            <Filter className="h-5 w-5 text-muted-foreground flex-shrink-0" />
            <button
              onClick={() => setSelectedGenre('all')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex-shrink-0 ${
                selectedGenre === 'all'
                  ? 'bg-electric text-background'
                  : 'bg-secondary text-foreground hover:bg-secondary/80'
              }`}
              data-testid="genre-filter-all"
            >
              All
            </button>
            {genres.map((genre) => (
              <button
                key={genre}
                onClick={() => setSelectedGenre(genre)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex-shrink-0 ${
                  selectedGenre === genre
                    ? 'bg-electric text-background'
                    : 'bg-secondary text-foreground hover:bg-secondary/80'
                }`}
                data-testid={`genre-filter-${genre.toLowerCase()}`}
              >
                {genre}
              </button>
            ))}
          </div>
        </div>

        {/* Search Modal */}
        <SearchModal
          isOpen={isSearchOpen}
          onClose={() => setIsSearchOpen(false)}
          allContent={videos}
          contentType="music videos"
        />

        {/* Results Count */}
        <div className="mb-6">
          <p className="text-muted-foreground">
            Showing {filteredVideos.length} {filteredVideos.length === 1 ? 'video' : 'videos'}
          </p>
        </div>

        {/* Videos Grid - 16:9 aspect ratio */}
        {filteredVideos.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6" data-testid="videos-grid">
            {filteredVideos.map((video) => (
              <MusicVideoCard key={video.id} content={video} navigate={navigate} />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-muted-foreground text-lg">No music videos found</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Music Video Card with 16:9 aspect ratio
const MusicVideoCard = ({ content, navigate }) => {
  return (
    <div
      className="group relative cursor-pointer rounded-2xl overflow-hidden bg-white/5 backdrop-blur-2xl border border-white/20 transition-all duration-300 hover:scale-105 hover:border-electric/60 hover:shadow-2xl hover:shadow-electric/40 hover:bg-white/10"
      style={{ backdropFilter: 'blur(30px) saturate(180%)' }}
      onClick={() => navigate(`/content/${content.id}`)}
      data-testid={`music-video-card-${content.id}`}
    >
      {/* 16:9 Aspect Ratio Thumbnail */}
      <div className="relative aspect-video overflow-hidden">
        <img
          src={content.thumbnail}
          alt={content.title}
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
        />
        
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-14 h-14 rounded-full bg-electric/90 flex items-center justify-center backdrop-blur-sm">
              <Play className="h-7 w-7 text-background fill-background" />
            </div>
          </div>
        </div>

        {/* Premium Badge */}
        {content.is_premium && (
          <div className="absolute top-3 right-3 bg-electric/95 backdrop-blur-xl px-3 py-1.5 rounded-lg flex items-center space-x-1 border border-white/30 shadow-lg" style={{ backdropFilter: 'blur(20px) saturate(180%)' }}>
            <Lock className="h-3 w-3 text-background" />
            <span className="text-xs font-semibold text-background">Premium</span>
          </div>
        )}

        {/* PPV Badge */}
        {content.is_ppv && (
          <div className="absolute top-3 left-3 bg-amber-500/95 backdrop-blur-xl px-3 py-1.5 rounded-lg flex items-center space-x-1 border border-white/30 shadow-lg" style={{ backdropFilter: 'blur(20px) saturate(180%)' }}>
            <DollarSign className="h-3 w-3 text-background" />
            <span className="text-xs font-semibold text-background">$4.99 PPV</span>
          </div>
        )}

        {/* Favorite Button */}
        <div className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
          <FavoriteButton contentId={content.id} size="sm" />
        </div>
      </div>

      {/* Content Info */}
      <div className="p-4">
        <h3 className="text-base font-semibold text-foreground line-clamp-1 mb-2">
          {content.title}
        </h3>
        
        <div className="flex items-center justify-between text-sm text-muted-foreground mb-2">
          <span>{content.year}</span>
          <div className="flex items-center space-x-1">
            <Star className="h-4 w-4 text-electric fill-electric" />
            <span>{content.rating.toFixed(1)}</span>
          </div>
        </div>
        
        {content.duration && (
          <p className="text-sm text-muted-foreground">{content.duration}</p>
        )}

        {/* Genres */}
        <div className="flex flex-wrap gap-1 mt-3">
          {content.genre.slice(0, 2).map((genre, index) => (
            <span
              key={index}
              className="px-2 py-1 rounded-full bg-white/10 text-xs text-foreground/70"
            >
              {genre}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MusicVideos;
